/**
 * News Service
 * Provides creator CRUD, admin assignment, public browsing, and visit statistics APIs for multilingual News.
 */

import api from './apiClient';
import { normalizeLanguageCode } from '../utils/languagePreference';
import { getAnonymousVisitorKey } from '../utils/anonymousVisitorKey';

const NEWS_VISITOR_KEY = 'heatcrafthp.newsVisitorKey';

/**
 * Get or create an anonymous visitor key for News statistics.
 * @returns {string} Stable browser-local anonymous visitor key
 */
export const getNewsVisitorKey = () => {
  try {
    const key = getAnonymousVisitorKey();
    if (!localStorage.getItem(NEWS_VISITOR_KEY)) {
      localStorage.setItem(NEWS_VISITOR_KEY, key);
    }
    return key;
  } catch (error) {
    return getAnonymousVisitorKey();
  }
};

/**
 * Append a value to FormData with backend model-binder-compatible keys.
 * @param {FormData} formData - Target form data
 * @param {string} key - Field key
 * @param {*} value - Field value
 */
const appendValue = (formData, key, value) => {
  if (value === null || value === undefined) return;
  if (value instanceof File) {
    formData.append(key, value);
  } else if (typeof value === 'boolean' || typeof value === 'number') {
    formData.append(key, value.toString());
  } else if (value instanceof Date) {
    formData.append(key, value.toISOString());
  } else {
    formData.append(key, value);
  }
};

/**
 * Build multipart form data for News create/update payloads.
 * @param {Object} data - News payload
 * @param {File|null} coverImage - Optional cover image
 * @returns {FormData}
 */
const buildNewsFormData = (data = {}, coverImage = null) => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (key === 'instances' && Array.isArray(value)) {
      value.forEach((instance, index) => {
        appendValue(formData, `Instances[${index}].CountryId`, instance.countryId);
        appendValue(formData, `Instances[${index}].Title`, instance.title);
        appendValue(formData, `Instances[${index}].Body`, instance.body);
      });
    } else {
      const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
      appendValue(formData, pascalKey, value);
    }
  });

  if (coverImage instanceof File) {
    formData.append('coverImage', coverImage);
  }

  return formData;
};

/**
 * Generate a slug from title-like text.
 * @param {string} value - Source text
 * @returns {string} URL-safe slug
 */
export const createNewsSlug = (value = '') => {
  const slug = String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'news';
};

/**
 * Build the public URL path for a News item.
 * @param {Object} news - News object with id and slug
 * @returns {string} Public URL path
 */
export const getNewsPublicPath = (news) => `/news/${news.id}/${news.slug || createNewsSlug(news.title || news.name)}`;

export const getNews = async (params = {}, options = {}) => api.get('/news', params, options);

export const getNewsDetail = async (id, options = {}) => api.get(`/news/${id}`, {}, options);

export const createNews = async (data, coverImage = null, options = {}) => {
  const formData = buildNewsFormData(data, coverImage);
  return api.upload('/news', formData, options);
};

export const updateNews = async (id, data, coverImage = null, options = {}) => {
  const formData = buildNewsFormData(data, coverImage);
  return api.uploadPut(`/news/${id}`, formData, options);
};

export const deleteNews = async (id, options = {}) => api.delete(`/news/${id}`, options);

export const publishNews = async (id, options = {}) => api.post(`/news/${id}/publish`, {}, options);

export const unpublishNews = async (id, options = {}) => api.post(`/news/${id}/unpublish`, {}, options);

export const getNewsStatistics = async (id, days = 30, options = {}) => api.get(`/news/${id}/statistics`, { days }, options);

export const getAssignedNewsCreators = async (params = {}, options = {}) => api.get('/news/admin/creators', params, options);

export const getAvailableNewsCreators = async (params = {}, options = {}) => api.get('/news/admin/available-creators', params, options);

export const assignNewsCreator = async (userId, options = {}) => api.post(`/news/admin/creators/${userId}`, {}, options);

export const revokeNewsCreator = async (userId, options = {}) => api.delete(`/news/admin/creators/${userId}`, options);

export const getPublicNews = async (params = {}, options = {}) => api.get('/news/public', params, options);

export const getPublicNewsDetail = async (id, slug = '', languageCode = 'en', options = {}) => {
  const normalized = normalizeLanguageCode(languageCode) || 'en';
  const pathSlug = slug ? `/${encodeURIComponent(slug)}` : '';
  return api.get(`/news/public/${id}${pathSlug}`, { languageCode: normalized }, options);
};

export const recordNewsVisit = async (id, payload = {}, options = {}) => {
  return api.post(`/news/public/${id}/visit`, {
    anonymousVisitorKey: getNewsVisitorKey(),
    ...payload,
  }, options);
};

const newsService = {
  getNewsVisitorKey,
  createNewsSlug,
  getNewsPublicPath,
  getNews,
  getNewsDetail,
  createNews,
  updateNews,
  deleteNews,
  publishNews,
  unpublishNews,
  getNewsStatistics,
  getAssignedNewsCreators,
  getAvailableNewsCreators,
  assignNewsCreator,
  revokeNewsCreator,
  getPublicNews,
  getPublicNewsDetail,
  recordNewsVisit,
};

export default newsService;
